import { useEffect, useRef, useCallback } from "react";

interface UseUserActivityOptions {
  onIdle: () => void;
  onActive: () => void;
  idleTimeout?: number;
}

export function useUserActivity({
  onIdle,
  onActive,
  idleTimeout = 5 * 60 * 1000,
}: UseUserActivityOptions) {
  const isIdle = useRef(false);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);
  const idleTimeoutRef = useRef(idleTimeout);

  useEffect(() => {
    onIdleRef.current = onIdle;
    onActiveRef.current = onActive;
    idleTimeoutRef.current = idleTimeout;
  }, [onIdle, onActive, idleTimeout]);

  const resetTimer = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }

    if (isIdle.current) {
      isIdle.current = false;
      onActiveRef.current();
    }

    timeoutId.current = setTimeout(() => {
      isIdle.current = true;
      onIdleRef.current();
    }, idleTimeoutRef.current);
  }, []);
  
  const resetTimerRef = useRef(resetTimer);
  resetTimerRef.current = resetTimer;

  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      resetTimerRef.current();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    resetTimerRef.current();

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  return { isIdle: isIdle.current };
}
